import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Checkbox, Drawer, Input, message, Modal, Pagination, Select, Skeleton, Spin, Table, Tabs, Upload } from 'antd'
import { UploadOutlined } from '@ant-design/icons'
import { FaArrowLeft, FaExchangeAlt, FaEye, FaHistory, FaLink, FaListUl, FaMapMarkedAlt, FaMapMarkerAlt, FaRedo, FaRoute, FaSearch, FaUnlink } from 'react-icons/fa'
import { GET, POST } from '../../utils/apis'
import { secureApi } from '../../Config/api'
import moment from 'moment'
import { verifyToken, vehicleLabel, fetchAllOrganization } from '../../utils/helpers'
import { allPaths, translations } from '../../utils/constants'
import { CustomModal, DeleteModal, GtfsVersionsModal, VehicleMap } from '../../Components'
import { DIRECTION_COLORS } from '../../Components/VehicleMap/VehicleMap'
import { SelectAllRow } from '../../Components/GtfsUpload/GtfsUpload'

// GTFS times are "HH:MM:SS" and may exceed 24:00 for after-midnight runs —
// show them raw (25:10 stays 25:10), only drop the seconds.
const formatGtfsTime = (time) => (time ? String(time).slice(0, 5) : '-')

const GtfsDetails = ({ language }) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()
  const user = useSelector((state) => state?.authReducer?.user)
  const canManage = ['admin', 'superAdmin'].includes(user?.role)
  const isSuperAdmin = user?.role === 'superAdmin'
  // Organization names for the superAdmin's read-only organization field in
  // the attach modal (the feed belongs to exactly one organization).
  const allOrganizations = useSelector((state) => state?.organizationReducer?.allOrganizations)
  // MenuLayout renders screens OUTSIDE a <Route> context, so useParams() is
  // always empty here — read the feed from the URL path instead.
  const feed = decodeURIComponent(
    location?.pathname?.startsWith(`${allPaths?.GTFS_DETAILS}/`)
      ? location.pathname.slice(allPaths.GTFS_DETAILS.length + 1)
      : ''
  )
  // Archived versions live under "<feed>__v<N>" — browsing one is read-only:
  // no vehicles are attached to it and nothing is live.
  const versionMatch = feed.match(/__v(\d+)$/)
  const versionNo = versionMatch ? parseInt(versionMatch[1], 10) : null
  const isVersion = !!versionMatch
  const baseFeed = isVersion ? feed.replace(/__v\d+$/, '') : feed

  const [versionsModal, setVersionsModal] = useState(false)

  const [lines, setLines] = useState([])
  const [loading, setLoading] = useState(false)
  const [selectedLine, setSelectedLine] = useState(null)
  const [lineDetail, setLineDetail] = useState(null)
  const [detailLoading, setDetailLoading] = useState(false)
  // Line drawer body: 'stops' (per-direction timelines) or 'map' (the line
  // drawn on a map, one direction at a time so its start/end read clearly).
  const [lineView, setLineView] = useState('stops')
  const [lineMapDirection, setLineMapDirection] = useState(null)
  // "Stop coordinates" modal: give an Excel-converted feed's placeholder
  // stops real positions (platform stops for the area + optional files).
  const [coordsModal, setCoordsModal] = useState(false)
  const [coordsFiles, setCoordsFiles] = useState([])
  const [coordsSaving, setCoordsSaving] = useState(false)
  const [coordsResult, setCoordsResult] = useState(null)
  // Geocode what no reference list places (name lookup, ~1/s, several calls).
  const [coordsGeocode, setCoordsGeocode] = useState(true)
  // Set by "Try again": the next run re-asks the geocoders about the names
  // they already gave up on (the number still unresolved is kept for the note).
  const [coordsRetry, setCoordsRetry] = useState(null)
  // "Detect lines" modal: per-vehicle line proposal from GPS history, the
  // operator's choice per vehicle, and which rows to apply.
  const [detectModal, setDetectModal] = useState(false)
  const [detectLoading, setDetectLoading] = useState(false)
  const [detectResult, setDetectResult] = useState(null)
  const [detectChoice, setDetectChoice] = useState({})
  const [detectSelected, setDetectSelected] = useState([])
  const [assignSaving, setAssignSaving] = useState(false)
  const [coordsProgress, setCoordsProgress] = useState(null)
  // Bumped on open/close so a running geocode loop stops with the modal.
  const coordsRun = useRef(0)
  const [lineShifts, setLineShifts] = useState([])
  const [shiftsLoading, setShiftsLoading] = useState(false)
  const [allShifts, setAllShifts] = useState([])
  const [allShiftsLoading, setAllShiftsLoading] = useState(false)
  // Which tab is showing — the Shifts tab loads its (feed-wide) data only
  // once opened, so the page's first paint waits for the lines alone.
  const [activeTab, setActiveTab] = useState('routes')
  const [expandedTripId, setExpandedTripId] = useState(null)
  // Journey (trip) whose stop list is open in the side drawer: { journey, shift }.
  const [journeyDrawer, setJourneyDrawer] = useState(null)
  const [tripStops, setTripStops] = useState({})
  const [tripStopsLoading, setTripStopsLoading] = useState(null)
  const [shiftsPage, setShiftsPage] = useState(1)
  const [journeyPages, setJourneyPages] = useState({})
  const [vehicles, setVehicles] = useState([])
  const [vehiclesLoading, setVehiclesLoading] = useState(false)
  const [selectedVehicle, setSelectedVehicle] = useState(null)
  // Vehicle drawer map: the router's live position + today's trace (same
  // call as the Devices drawer) and the assigned line's directions.
  const [vehicleDeviceInfo, setVehicleDeviceInfo] = useState(null)
  const [vehicleDeviceLoading, setVehicleDeviceLoading] = useState(false)
  const [vehicleLineDirections, setVehicleLineDirections] = useState([])
  const [vehicleLineLoading, setVehicleLineLoading] = useState(false)
  // Stable "today" for the drawer map - a fresh Date per render would retrigger
  // VehicleMap's schedule polling on every render. Reset per opened vehicle.
  const selectedVehicleId = selectedVehicle?.id
  const [vehicleMapDate, setVehicleMapDate] = useState(() => new Date())

  useEffect(() => {
    setAssignmentHistory([])
    if (selectedVehicleId && canManage) fetchAssignmentHistory(selectedVehicleId)
  }, [selectedVehicleId])

  // Vehicle attach/detach state (Attached Vehicles tab).
  const [feeds, setFeeds] = useState([])
  const [attachModal, setAttachModal] = useState(false)
  const [attachOptions, setAttachOptions] = useState([])
  const [attachVehicleIds, setAttachVehicleIds] = useState([])
  // Attached Vehicles tab: free-text search + client-side page (the list is
  // small enough to hold whole; the endpoint returns the feed's vehicles).
  const [vehicleSearch, setVehicleSearch] = useState('')
  const [vehiclesPageNo, setVehiclesPageNo] = useState(1)
  // Routes tab: same search box; matches line code, route name, route id.
  const [lineSearch, setLineSearch] = useState('')
  const [linesPageNo, setLinesPageNo] = useState(1)
  const [attachOptionsLoading, setAttachOptionsLoading] = useState(false)
  const [attachSaving, setAttachSaving] = useState(false)
  const [detachTarget, setDetachTarget] = useState(null)
  const [detachSaving, setDetachSaving] = useState(false)
  // "Change line / trip": real-time reassignment of one vehicle. The trip
  // list is the shifts (turni) of the chosen line for today.
  const [reassignTarget, setReassignTarget] = useState(null)
  const [reassignRoute, setReassignRoute] = useState(null)
  const [reassignTrip, setReassignTrip] = useState(null)
  const [reassignShifts, setReassignShifts] = useState([])
  const [reassignShiftsLoading, setReassignShiftsLoading] = useState(false)
  const [reassignForce, setReassignForce] = useState(false)
  const [reassignReason, setReassignReason] = useState('')
  const [reassignSaving, setReassignSaving] = useState(false)
  const [assignmentHistory, setAssignmentHistory] = useState([])

  // Org feeds are named "org-<id>" (see uploadGtfsForOrganization) — the org id
  // falls back to the feeds list for anything else. superAdmin needs it on
  // every org-scoped call.
  const feedOrgId = baseFeed?.startsWith('org-')
    ? baseFeed.slice(4)
    : feeds?.find((f) => f?.feed_source === baseFeed)?.organization_id || user?.organization_id
  // Shown in the header for superAdmin, who sees every organization's feeds.
  const feedOrgName = isSuperAdmin
    ? allOrganizations?.find((o) => String(o?.id) === String(feedOrgId))?.organization_name
    : null

  useEffect(() => {
    verifyToken(history, dispatch)
    // The feed comes from the URL — switching between versions pushes a new
    // path without remounting (MenuLayout renders screens outside <Route>),
    // so reset and refetch whenever it changes.
    setSelectedLine(null)
    setLineDetail(null)
    setLineShifts([])
    setExpandedTripId(null)
    setTripStops({})
    setShiftsPage(1)
    setJourneyPages({})
    setAllShifts([])
    fetchLines()
    if (isVersion) return undefined // archived versions are static — nothing live to refresh
    fetchFeeds()
    fetchVehicles()
    if (isSuperAdmin && !allOrganizations?.length) fetchAllOrganization({ dispatch })
    return undefined
  }, [feed])

  // The Shifts tab is the heaviest call of the page (every journey of the
  // feed plus the live-vehicle check): fetch it when the tab is opened and
  // keep it fresh only while it is showing.
  useEffect(() => {
    if (activeTab !== 'shifts') return undefined
    fetchAllShifts()
    if (isVersion) return undefined
    const interval = setInterval(() => fetchAllShifts(true), 60000)
    return () => clearInterval(interval)
  }, [activeTab, feed])

  // Keep the live journey highlight fresh while the line drawer is open —
  // the backend derives it from the vehicles' current matched trip.
  useEffect(() => {
    if (isVersion || !selectedLine?.route_id) return undefined
    const interval = setInterval(() => fetchShifts(selectedLine.route_id, true), 60000)
    return () => clearInterval(interval)
  }, [selectedLine])

  const fetchLines = async () => {
    setLoading(true)
    try {
      const response = await secureApi?.get(GET?.GET_GTFS_LINES, {
        params: { feed_source: feed },
      })
      if (response?.success) {
        setLines(response?.data || [])
      } else {
        message.error(response?.message || 'Failed to fetch GTFS lines.')
      }
    } catch (error) {
      message.error('Error fetching GTFS lines.')
    } finally {
      setLoading(false)
    }
  }

  const fetchVehicles = async () => {
    setVehiclesLoading(true)
    try {
      const response = await secureApi?.get(GET?.GET_GTFS_VEHICLES, {
        params: { feed_source: feed, organization_id: feedOrgId },
      })
      if (response?.success) {
        setVehicles(response?.data || [])
      } else {
        message.error(response?.message || 'Failed to fetch attached vehicles.')
      }
    } catch (error) {
      message.error('Error fetching attached vehicles.')
    } finally {
      setVehiclesLoading(false)
    }
  }

  // Feeds list names the GTFS a locked vehicle sits on (attach modal) and
  // resolves the feed's organization for superAdmin calls.
  const fetchFeeds = async () => {
    try {
      const response = await secureApi?.get(GET?.GET_GTFS_FEEDS)
      if (response?.success) {
        setFeeds(response?.data || [])
      }
    } catch (error) {
      // best effort — locked vehicles then show the raw feed id
    }
  }

  // Vehicles of the feed's organization that aren't on THIS feed yet. Those
  // sitting on another feed are listed but locked: a vehicle stays with its
  // GTFS until detached there (the backend refuses to move it). The
  // organization filter endpoint is used because the plain vehicles endpoint
  // hands a superAdmin the whole fleet regardless of organization_id.
  const openAttachModal = async () => {
    setAttachModal(true)
    setAttachVehicleIds([])
    setAttachOptions([])
    setAttachOptionsLoading(true)
    try {
      const response = await secureApi?.get(GET?.GET_ORGANIZATION_FILTER, {
        params: {
          user_id: user?.id,
          resourceType: 'vehicles',
          page: 1,
          limit: 1000,
          searchQuery: '',
          organization_id: feedOrgId,
        },
      })
      const all = response?.success ? response?.data || [] : []
      setAttachOptions(all.filter((v) => v?.gtfs_feed_source !== baseFeed))
    } catch (error) {
      message.error('Error fetching vehicles.')
    } finally {
      setAttachOptionsLoading(false)
    }
  }

  // The attach/move response carries per-vehicle line detection results —
  // surface them so the user knows whether a route was pinned right away.
  const detectionNote = (det) => {
    if (!det) return ''
    if (det?.matched) return ` — ${translations[language]?.lineDetected}: ${det?.route_short_name || det?.route_id}`
    if (det?.history_assigned)
      return ` — ${translations[language]?.lineSuggested}: ${det?.route_short_name || det?.route_id}`
    if (det?.reason === 'vehicle_not_moving' || det?.reason === 'no_gps_today')
      return ` — ${translations[language]?.linePendingNotMoving}`
    return ''
  }

  const handleAttach = async () => {
    if (!attachVehicleIds.length) return
    setAttachSaving(true)
    try {
      const response = await secureApi?.post(POST?.ATTACH_GTFS_VEHICLES, {
        feed_source: baseFeed,
        vehicle_ids: attachVehicleIds,
        organization_id: feedOrgId,
      })
      if (response?.success) {
        // Detection detail only makes sense for a single vehicle; a bulk
        // attach just reports success (detections land in the table on refresh).
        const note =
          attachVehicleIds.length === 1 ? detectionNote(response?.data?.detections?.[0]) : ''
        message.success(`${translations[language]?.vehicleAttached}${note}`)
        setAttachModal(false)
        fetchVehicles()
      } else {
        message.error(response?.message || 'Failed to attach vehicle.')
      }
    } catch (error) {
      message.error('Error attaching vehicle.')
    } finally {
      setAttachSaving(false)
    }
  }

  // Attach-modal helpers: only vehicles with no GTFS can be picked; the rest
  // show which feed holds them.
  const attachableOptions = attachOptions.filter((v) => !v?.gtfs_feed_source)
  const feedNameOf = (feedSource) =>
    feeds?.find((f) => f?.feed_source === feedSource)?.feed_name || feedSource

  const handleDetach = async () => {
    if (!detachTarget || detachSaving) return
    setDetachSaving(true)
    try {
      const response = await secureApi?.post(POST?.ATTACH_GTFS_VEHICLES, {
        vehicle_ids: [detachTarget?.id],
        detach: true,
        organization_id: feedOrgId,
      })
      if (response?.success) {
        message.success(translations[language]?.vehicleDetached)
        setDetachTarget(null)
        fetchVehicles()
      } else {
        message.error(response?.message || 'Failed to detach vehicle.')
      }
    } catch (error) {
      message.error('Error detaching vehicle.')
    } finally {
      setDetachSaving(false)
    }
  }

  // ---- Change line / trip (real-time reassignment) ----
  const closeReassign = () => {
    setReassignTarget(null)
    setReassignRoute(null)
    setReassignTrip(null)
    setReassignShifts([])
    setReassignForce(false)
    setReassignReason('')
  }

  const fetchReassignShifts = async (routeId) => {
    if (!routeId) {
      setReassignShifts([])
      return
    }
    setReassignShiftsLoading(true)
    try {
      const response = await secureApi?.get(GET?.GET_GTFS_LINE_SHIFTS, {
        params: { feed_source: feed, route_id: routeId },
      })
      setReassignShifts(response?.success ? response?.data?.shifts || [] : [])
    } catch (error) {
      setReassignShifts([])
    } finally {
      setReassignShiftsLoading(false)
    }
  }

  const fetchAssignmentHistory = async (vehicleId) => {
    if (!vehicleId) return
    try {
      const response = await secureApi?.get(`${GET?.VEHICLE_ASSIGNMENT_HISTORY}/${vehicleId}/assignment-history`, {
        params: { limit: 20, organization_id: feedOrgId },
      })
      setAssignmentHistory(response?.success ? response?.data || [] : [])
    } catch (error) {
      setAssignmentHistory([])
    }
  }

  const openReassign = (record) => {
    setReassignTarget(record)
    setReassignRoute(record?.gtfs_route_id || null)
    setReassignTrip(null)
    setReassignForce(false)
    setReassignReason('')
    fetchReassignShifts(record?.gtfs_route_id || null)
  }

  // Today's journeys of the chosen line, flat, with the shift (block) and
  // the vehicle already on it when there is one.
  const reassignTripOptions = (() => {
    const t = translations[language] || {}
    const out = []
    for (const sh of reassignShifts) {
      for (const j of sh?.journeys || []) {
        const busy = Boolean(j?.live && j?.vehicle_id && j.vehicle_id !== reassignTarget?.id)
        const parts = [`${j?.starts || '?'}–${j?.ends || '?'}`, j?.headsign || j?.trip_short_name || j?.trip_id]
        if (sh?.block_id) parts.push(`${t.shift} ${sh.block_id}`)
        if (busy) parts.push(`${t.inUseBy} ${j?.vehicle_name || j?.registering_number || ''}`.trim())
        out.push({ value: j?.trip_id, label: parts.join(' · '), disabled: busy && !reassignForce })
      }
    }
    return out
  })()

  const reassignLine = lines?.find((l) => String(l?.route_id) === String(reassignRoute)) || null
  const reassignLineLabel = reassignLine
    ? `${reassignLine.line || reassignLine.route_id}${reassignLine.route_long_name ? ` ${reassignLine.route_long_name}` : ''}`
    : reassignRoute || ''

  const handleReassign = async () => {
    if (!reassignTarget || reassignSaving || !reassignRoute) return
    setReassignSaving(true)
    try {
      const body = {
        route_id: reassignRoute,
        trip_id: reassignTrip || null,
        force: reassignForce,
        reason: reassignReason || null,
        effective_from: new Date().toISOString(),
      }
      if (isSuperAdmin && feedOrgId) body.organization_id = feedOrgId
      const response = await secureApi?.patch(
        `${POST?.REASSIGN_VEHICLE_TRIP}/${reassignTarget.id}/reassign-trip`,
        body
      )
      if (response?.success) {
        message.success(translations[language]?.lineChanged)
        const v = response?.data?.vehicle
        if (v) {
          // Reflect it immediately; the refetch below reconciles the rest.
          setVehicles((prev) => prev.map((x) => (x?.id === v.id ? { ...x, ...v } : x)))
          setSelectedVehicle((prev) => (prev?.id === v.id ? { ...prev, ...v } : prev))
        }
        closeReassign()
        fetchVehicles()
        if (selectedVehicleId) fetchAssignmentHistory(selectedVehicleId)
        if (selectedLine?.route_id) fetchShifts(selectedLine.route_id, true)
      } else {
        message.error(response?.message || translations[language]?.lineChangeFailed)
      }
    } catch (error) {
      message.error(translations[language]?.lineChangeFailed)
    } finally {
      setReassignSaving(false)
    }
  }

  // Live public data changes on apply — always confirm first.
  const confirmReassign = () => {
    if (!reassignRoute) {
      message.warning(translations[language]?.selectLine)
      return
    }
    const t = translations[language] || {}
    const tripLabel = reassignTrip
      ? reassignTripOptions.find((o) => o.value === reassignTrip)?.label || reassignTrip
      : t.wholeLine
    Modal.confirm({
      title: t.confirmLineChange,
      content: `${t.confirmLineChangeText} "${reassignTarget?.vehicle_name || reassignTarget?.license_plate || ''}" ${t.toLine} ${reassignLineLabel} (${tripLabel})?`,
      okText: t.applyChange,
      cancelText: t.cancelButton,
      onOk: handleReassign,
    })
  }

  const fetchShifts = async (routeId, silent = false) => {
    if (!silent) setShiftsLoading(true)
    try {
      const response = await secureApi?.get(GET?.GET_GTFS_LINE_SHIFTS, {
        params: { feed_source: feed, route_id: routeId },
      })
      if (response?.success) {
        setLineShifts(response?.data?.shifts || [])
      } else if (!silent) {
        message.error(response?.message || 'Failed to fetch line shifts.')
      }
    } catch (error) {
      if (!silent) message.error('Error fetching line shifts.')
    } finally {
      if (!silent) setShiftsLoading(false)
    }
  }

  const toggleJourney = async (journey, shift) => {
    const tripId = journey?.trip_id
    if (!tripId) return
    setExpandedTripId(tripId)
    setJourneyDrawer({ journey, shift })
    if (tripStops[tripId]) return // already loaded once
    setTripStopsLoading(tripId)
    try {
      const response = await secureApi?.get(GET?.GET_GTFS_TRIP_STOPS, {
        params: { feed_source: feed, trip_id: tripId },
      })
      if (response?.success) {
        setTripStops((prev) => ({ ...prev, [tripId]: response?.data?.stops || [] }))
      } else {
        message.error(response?.message || 'Failed to fetch journey stops.')
      }
    } catch (error) {
      message.error('Error fetching journey stops.')
    } finally {
      setTripStopsLoading(null)
    }
  }

  const fetchAllShifts = async (silent = false) => {
    if (!silent) setAllShiftsLoading(true)
    try {
      const response = await secureApi?.get(GET?.GET_GTFS_LINE_SHIFTS, {
        params: { feed_source: feed },
      })
      if (response?.success) {
        setAllShifts(response?.data?.shifts || [])
      } else if (!silent) {
        message.error(response?.message || 'Failed to fetch shifts.')
      }
    } catch (error) {
      if (!silent) message.error('Error fetching shifts.')
    } finally {
      if (!silent) setAllShiftsLoading(false)
    }
  }

  const openLine = async (record) => {
    setSelectedLine(record)
    setLineDetail(null)
    setLineShifts([])
    setLineView('stops')
    setLineMapDirection(null)
    setDetailLoading(true)
    fetchShifts(record?.route_id)
    try {
      const response = await secureApi?.get(GET?.GET_GTFS_LINE_DETAIL, {
        params: { feed_source: feed, route_id: record?.route_id },
      })
      if (response?.success) {
        setLineDetail(response?.data)
      } else {
        message.error(response?.message || 'Failed to fetch line detail.')
      }
    } catch (error) {
      message.error('Error fetching line detail.')
    } finally {
      setDetailLoading(false)
    }
  }

  const closeDrawer = () => {
    setSelectedLine(null)
    setLineDetail(null)
    setLineShifts([])
    setLineView('stops')
    setLineMapDirection(null)
  }

  // Load what the vehicle drawer's map needs whenever a vehicle is opened:
  // its router's position (if it has one) and its line's geometry (if it
  // is pinned to a line). Either may be missing - the map copes with both.
  const selectedVehicleSerial = selectedVehicle?.serial_number
  const selectedVehicleRouteId = selectedVehicle?.gtfs_route_id
  useEffect(() => {
    if (!selectedVehicleId) {
      setVehicleDeviceInfo(null)
      setVehicleLineDirections([])
      setVehicleDeviceLoading(false)
      setVehicleLineLoading(false)
      return undefined
    }
    let cancelled = false
    setVehicleMapDate(new Date())

    // The device-info call returns the raw GPS trace first (snapped=false)
    // while the road-snapped version is computed server-side; poll a few
    // times until it flips so the drawer ends up with the road line.
    let snapTimer = null
    const loadPosition = async (attempt = 0) => {
      if (!selectedVehicleSerial) {
        setVehicleDeviceInfo(null)
        return
      }
      if (attempt === 0) setVehicleDeviceLoading(true)
      try {
        const response = await secureApi?.get(GET?.GET_DEVICE_INFO, {
          params: { serialNumber: selectedVehicleSerial },
        })
        if (cancelled) return
        setVehicleDeviceInfo(response?.success ? response?.data : null)
        if (response?.success && response?.data?.snapped === false && attempt < 5) {
          snapTimer = setTimeout(() => loadPosition(attempt + 1), 4000)
        }
      } catch (error) {
        if (!cancelled && attempt === 0) setVehicleDeviceInfo(null)
      } finally {
        if (!cancelled && attempt === 0) setVehicleDeviceLoading(false)
      }
    }

    const loadLine = async () => {
      if (!selectedVehicleRouteId) {
        setVehicleLineDirections([])
        return
      }
      setVehicleLineLoading(true)
      try {
        const response = await secureApi?.get(GET?.GET_GTFS_LINE_DETAIL, {
          params: { feed_source: feed, route_id: selectedVehicleRouteId },
        })
        if (!cancelled) {
          setVehicleLineDirections(response?.success ? response?.data?.directions || [] : [])
        }
      } catch (error) {
        if (!cancelled) setVehicleLineDirections([])
      } finally {
        if (!cancelled) setVehicleLineLoading(false)
      }
    }

    setVehicleDeviceInfo(null)
    setVehicleLineDirections([])
    loadPosition()
    loadLine()
    return () => {
      cancelled = true
      if (snapTimer) clearTimeout(snapTimer)
    }
  }, [selectedVehicleId, selectedVehicleSerial, selectedVehicleRouteId, feed])

  const hasVehicleLocation =
    Number.isFinite(parseFloat(vehicleDeviceInfo?.lat)) &&
    Number.isFinite(parseFloat(vehicleDeviceInfo?.lng))
  const vehicleMapLoading = vehicleDeviceLoading || vehicleLineLoading

  const lineBadge = (text) => (
    <span
      style={{
        background: '#ED2328',
        color: '#fff',
        borderRadius: 4,
        padding: '2px 10px',
        fontWeight: 700,
        display: 'inline-block',
      }}
    >
      {text}
    </span>
  )

  const openCoordsModal = () => {
    coordsRun.current += 1
    setCoordsResult(null)
    setCoordsProgress(null)
    setCoordsRetry(null)
    setCoordsFiles([])
    setCoordsModal(true)
  }

  const closeCoordsModal = () => {
    coordsRun.current += 1
    setCoordsModal(false)
  }

  // One call resolves from the reference lists and geocodes up to ~25 names;
  // the server says how many names are still untried, so keep calling until
  // none are left (or the modal is closed). Counts are summed across calls.
  const handleResolveCoords = async () => {
    const run = ++coordsRun.current
    setCoordsSaving(true)
    let totalResolved = 0
    let totalGeocoded = 0
    let totalCached = 0
    try {
      for (let round = 0; round < 60; round += 1) {
        const formData = new FormData()
        formData.append('feed_source', feed)
        if (isSuperAdmin && feedOrgId) formData.append('organization_id', feedOrgId)
        coordsFiles.forEach((f) => formData.append('stops', f))
        if (coordsGeocode) formData.append('geocode', 'true')
        if (coordsRetry) formData.append('retry_misses', 'true')
        const response = await secureApi?.post(POST?.RESOLVE_GTFS_STOP_COORDINATES, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        if (run !== coordsRun.current) return
        if (!response?.success) {
          message.error(
            [response?.message, response?.error].filter(Boolean).join(': ') ||
              translations[language]?.coordsFailed
          )
          break
        }
        const data = response?.data || {}
        totalResolved += data.resolved || 0
        totalGeocoded += data.geocoded || 0
        totalCached += data.geocode_cached || 0
        setCoordsResult({
          ...data,
          resolved: totalResolved,
          geocoded: totalGeocoded,
          geocode_cached: totalCached,
        })
        if (!(data.geocode_remaining > 0)) {
          setCoordsProgress(null)
          break
        }
        setCoordsProgress({ remaining: data.geocode_remaining })
      }
      if (totalResolved > 0) {
        message.success(translations[language]?.coordsDone)
        // The open drawer and the lines table both read stop positions.
        closeDrawer()
        fetchLines()
      }
    } catch (error) {
      if (run === coordsRun.current) {
        message.error(error?.response?.data?.message || translations[language]?.coordsFailed)
      }
    } finally {
      if (run === coordsRun.current) {
        setCoordsSaving(false)
        setCoordsProgress(null)
      }
    }
  }

  const runDetection = async () => {
    setDetectLoading(true)
    try {
      const body = { feed_source: feed }
      if (isSuperAdmin && feedOrgId) body.organization_id = feedOrgId
      const response = await secureApi?.post(POST?.DETECT_GTFS_VEHICLE_LINES, body)
      if (response?.success) {
        const data = response?.data || {}
        setDetectResult(data)
        // Proposal per vehicle: the detected line, else what it has now.
        const choice = {}
        const selected = []
        for (const v of data.vehicles || []) {
          choice[v.vehicle_id] = v.detected?.route_id || v.current_route_id || null
          if (v.detected?.route_id && v.detected.route_id !== v.current_route_id) selected.push(v.vehicle_id)
        }
        setDetectChoice(choice)
        setDetectSelected(selected)
      } else {
        message.error(response?.message || translations[language]?.assignFailed)
      }
    } catch (error) {
      message.error(error?.response?.data?.message || translations[language]?.assignFailed)
    } finally {
      setDetectLoading(false)
    }
  }

  const openDetectModal = () => {
    setDetectResult(null)
    setDetectChoice({})
    setDetectSelected([])
    setDetectModal(true)
    runDetection()
  }

  const handleAssignLines = async () => {
    if (!detectSelected.length) {
      message.warning(translations[language]?.nothingSelected)
      return
    }
    setAssignSaving(true)
    try {
      const body = {
        feed_source: feed,
        assignments: detectSelected.map((id) => ({ vehicle_id: id, route_id: detectChoice[id] || null })),
      }
      if (isSuperAdmin && feedOrgId) body.organization_id = feedOrgId
      const response = await secureApi?.post(POST?.ASSIGN_GTFS_VEHICLE_LINES, body)
      if (response?.success) {
        message.success(`${translations[language]?.linesAssigned}: ${response?.data?.updated ?? 0}`)
        // Reflect the new assignment in the proposal table and the tab.
        const byId = new Map((response?.data?.vehicles || []).map((v) => [v.id, v]))
        setDetectResult((prev) =>
          prev
            ? {
                ...prev,
                vehicles: prev.vehicles.map((v) =>
                  byId.has(v.vehicle_id)
                    ? {
                        ...v,
                        current_route_id: byId.get(v.vehicle_id).gtfs_route_id || null,
                        current_line: byId.get(v.vehicle_id).line || null,
                        current_confirmed: false,
                      }
                    : v
                ),
              }
            : prev
        )
        setDetectSelected([])
        fetchVehicles()
      } else {
        message.error(response?.message || translations[language]?.assignFailed)
      }
    } catch (error) {
      message.error(error?.response?.data?.message || translations[language]?.assignFailed)
    } finally {
      setAssignSaving(false)
    }
  }

  // Line drawer's map view: direction chips (one colour each, matching the
  // map legend), the chosen direction drawn with S/E capolinea markers and
  // stop dots (names at street zoom or on click - same as the main map), and
  // the first/last stop spelled out under the map.
  const renderLineMap = () => {
    const directions = lineDetail?.directions || []
    const activeIndex = Math.max(
      0,
      directions.findIndex((d) => String(d?.direction_id) === String(lineMapDirection))
    )
    const active = directions[activeIndex]
    const color = DIRECTION_COLORS[activeIndex % DIRECTION_COLORS.length]
    const stops = (active?.stops || []).filter(
      (s) => Number.isFinite(parseFloat(s?.stop_lat)) && Number.isFinite(parseFloat(s?.stop_lng))
    )
    // Excel-converted feeds without a stops file park every stop on one
    // placeholder coordinate: nothing to draw (the map would just zoom onto
    // a single point), so say why instead.
    const distinctCoords = new Set(
      stops.map((s) => `${parseFloat(s.stop_lat).toFixed(5)},${parseFloat(s.stop_lng).toFixed(5)}`)
    ).size
    const drawable = (active?.shape || []).length > 1 || distinctCoords > 1
    const emptyReason =
      stops.length > 0 && distinctCoords <= 1
        ? translations[language]?.noStopCoordinates
        : translations[language]?.noLineGeometry
    const first = active?.stops?.[0]
    const last = active?.stops?.[active.stops.length - 1]
    return (
      <div className='gl-direction gl-map-card'>
        {directions.length > 1 && (
          <div className='gl-map-dirs'>
            {directions.map((d, i) => {
              const c = DIRECTION_COLORS[i % DIRECTION_COLORS.length]
              return (
                <button
                  type='button'
                  key={d?.direction_id ?? i}
                  className={`gl-map-dir${i === activeIndex ? ' active' : ''}`}
                  style={{ '--dir-color': c }}
                  onClick={() => setLineMapDirection(d?.direction_id)}
                >
                  <span className='gl-map-dir-swatch' style={{ background: c }} />
                  <span>{d?.headsign || `${translations[language]?.direction} ${d?.direction_id}`}</span>
                </button>
              )
            })}
          </div>
        )}
        {active && drawable ? (
          <div className='device-drawer-map'>
            <VehicleMap
              deviceInfo={null}
              vehicleDetails={null}
              language={language}
              selectedDate={vehicleMapDate}
              isLoading={false}
              lineDirections={[{ ...active, color }]}
              showLineEndpoints
            />
          </div>
        ) : (
          <div className='device-drawer-map-placeholder device-drawer-map-empty gl-map-empty'>
            <span>{emptyReason}</span>
            {!isVersion && canManage && stops.length > 0 && distinctCoords <= 1 && (
              <button type='button' className='add-button' onClick={openCoordsModal}>
                <FaMapMarkerAlt className='icons' />
                {translations[language]?.resolveCoordinates}
              </button>
            )}
          </div>
        )}
        {first && last && (
          <div className='gl-map-ends'>
            <div className='gl-map-end'>
              <span className='gl-map-end-badge gl-map-end-start'>S</span>
              <span className='gl-map-end-label'>{translations[language]?.startStop}</span>
              <strong>{first?.stop_name || first?.gtfs_stop_id}</strong>
              <span className='gl-map-end-time'>{formatGtfsTime(first?.departure_time || first?.arrival_time)}</span>
            </div>
            <div className='gl-map-end'>
              <span className='gl-map-end-badge gl-map-end-finish'>E</span>
              <span className='gl-map-end-label'>{translations[language]?.endStop}</span>
              <strong>{last?.stop_name || last?.gtfs_stop_id}</strong>
              <span className='gl-map-end-time'>{formatGtfsTime(last?.arrival_time || last?.departure_time)}</span>
            </div>
            <span className='gl-chip gl-chip-sm gl-map-ends-count'>
              <strong>{active?.stops?.length || 0}</strong> {translations[language]?.stops}
            </span>
          </div>
        )}
      </div>
    )
  }

  // Shared renderer: a shift (turno) card with all its journeys; the journey
  // being traveled right now is green with the vehicle running it. Journeys
  // are paginated inside the card (a shift can hold 200+ corse).
  const JOURNEYS_PAGE_SIZE = 10
  const SHIFTS_PAGE_SIZE = 5

  const renderShiftCards = (shifts, showLine) => (
    <div className='gs-list'>
      {shifts.map((shift) => {
        const shiftKey = `${shift?.route_id}-${shift?.block_id || 'unassigned'}`
        const journeys = shift?.journeys || []
        const jPage = Math.min(
          journeyPages[shiftKey] || 1,
          Math.max(1, Math.ceil(journeys.length / JOURNEYS_PAGE_SIZE))
        )
        const pagedJourneys = journeys.slice(
          (jPage - 1) * JOURNEYS_PAGE_SIZE,
          jPage * JOURNEYS_PAGE_SIZE
        )
        return (
          <div key={shiftKey} className={`gs-shift${shift?.live ? ' gs-shift-live' : ''}`}>
            <div className='gs-shift-head'>
              <div className='gs-shift-title'>
                {showLine && shift?.line ? lineBadge(shift.line) : null}
                <span className='gs-shift-name'>
                  {shift?.block_id
                    ? `${translations[language]?.shift} ${shift.block_id}`
                    : translations[language]?.noShiftAssigned}
                </span>
                {shift?.live && (
                  <span className='dash-status-pill dash-status-ok'>
                    <span className='dash-status-dot' />
                    {translations[language]?.liveNow}
                  </span>
                )}
              </div>
              <div className='gs-shift-meta'>
                <span className='gl-chip gl-chip-sm'>
                  <strong>{shift?.starts || '-'}</strong> - <strong>{shift?.ends || '-'}</strong>
                </span>
                <span className='gl-chip gl-chip-sm'>
                  <strong>{shift?.journeys_count}</strong> {translations[language]?.trips}
                </span>
              </div>
            </div>

            <div className='gs-journeys'>
              {pagedJourneys.map((journey) => {
                const isOpen = journeyDrawer?.journey?.trip_id === journey?.trip_id
                return (
                  <div
                    key={journey?.trip_id}
                    onClick={() => toggleJourney(journey, shift)}
                    className={`gs-journey${journey?.live ? ' gs-journey-live' : ''}${isOpen ? ' gs-journey-open' : ''}`}
                  >
                    <span className='gs-journey-time'>
                      {journey?.starts || '-'}
                      <span className='gs-journey-time-sep'>-</span>
                      {journey?.ends || '-'}
                    </span>
                    <span className='gs-journey-head'>{journey?.headsign || journey?.trip_id}</span>
                    {journey?.live && (
                      <span className='gs-journey-live-tag'>
                        <span className='dash-status-dot' />
                        {translations[language]?.liveNow}
                        {journey?.vehicle_name ? ` · ${journey.vehicle_name}` : ''}
                      </span>
                    )}
                    <span className='gs-journey-open-btn'>
                      <FaEye size={13} />
                    </span>
                  </div>
                )
              })}
            </div>

            {journeys.length > JOURNEYS_PAGE_SIZE && (
              <div className='gs-journeys-pager'>
                <Pagination
                  size='small'
                  current={jPage}
                  pageSize={JOURNEYS_PAGE_SIZE}
                  total={journeys.length}
                  showSizeChanger={false}
                  onChange={(page) =>
                    setJourneyPages((prev) => ({ ...prev, [shiftKey]: page }))
                  }
                />
              </div>
            )}
          </div>
        )
      })}
    </div>
  )

  const columns = [
    {
      title: translations[language]?.routes,
      dataIndex: 'line',
      key: 'line',
      width: 110,
      render: (text, record) => (
        <div onClick={() => openLine(record)} style={{ cursor: 'pointer' }}>
          {lineBadge(text)}
        </div>
      ),
    },
    {
      title: translations[language]?.details,
      dataIndex: 'route_long_name',
      key: 'routeLongName',
      render: (text, record) => (
        <span onClick={() => openLine(record)} style={{ cursor: 'pointer' }}>{text || '-'}</span>
      ),
    },
    {
      title: translations[language]?.trips,
      dataIndex: 'trips',
      key: 'trips',
    },
    {
      title: translations[language]?.stops,
      dataIndex: 'stops_served',
      key: 'stopsServed',
    },
    {
      title: translations[language]?.firstDeparture,
      dataIndex: 'first_departure',
      key: 'firstDeparture',
      render: (text) => <span>{formatGtfsTime(text)}</span>,
    },
    {
      title: translations[language]?.lastArrival,
      dataIndex: 'last_arrival',
      key: 'lastArrival',
      render: (text) => <span>{formatGtfsTime(text)}</span>,
    },
  ]

  const visibleLines = useMemo(() => {
    const q = lineSearch.trim().toLowerCase()
    if (!q) return lines || []
    return (lines || []).filter((l) =>
      [l?.line, l?.route_long_name, l?.route_id, l?.route_short_name]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(q)
    )
  }, [lines, lineSearch])

  // Rows of the Attached Vehicles table: filtered by the search box (name,
  // plate, registering number, line, route) and ordered with the vehicles
  // whose route has been detected first (by line, then name); vehicles still
  // waiting for a detection sink to the bottom.
  const visibleVehicles = useMemo(() => {
    const q = vehicleSearch.trim().toLowerCase()
    const hasRoute = (v) => Boolean(v?.line || v?.route_long_name || v?.gtfs_route_id)
    const text = (v) =>
      [v?.vehicle_name, v?.license_plate, v?.registering_number, v?.line, v?.route_long_name]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
    const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' })
    return (vehicles || [])
      .filter((v) => !q || text(v).includes(q))
      .sort((a, b) => {
        const ra = hasRoute(a) ? 0 : 1
        const rb = hasRoute(b) ? 0 : 1
        if (ra !== rb) return ra - rb
        return (
          collator.compare(String(a?.line || ''), String(b?.line || '')) ||
          collator.compare(String(a?.vehicle_name || ''), String(b?.vehicle_name || '')) ||
          collator.compare(String(a?.registering_number || ''), String(b?.registering_number || ''))
        )
      })
  }, [vehicles, vehicleSearch])

  const vehicleColumns = [
    {
      title: translations[language]?.vehicleName,
      dataIndex: 'vehicle_name',
      key: 'vehicleName',
      render: (text, record) => (
        <div className='vl-name-cell' onClick={() => setSelectedVehicle(record)}>
          <span className='vl-name-text capitalized-name'>{text || record?.license_plate || '-'}</span>
          <FaEye className='vl-name-view' />
        </div>
      ),
    },
    {
      title: translations[language]?.licensePlate,
      dataIndex: 'license_plate',
      key: 'licensePlate',
      render: (text) => (text ? <span className='vl-plate'>{text}</span> : <span className='vl-muted'>-</span>),
    },
    {
      title: translations[language]?.registeringNumber,
      dataIndex: 'registering_number',
      key: 'registeringNumber',
      render: (text) => (text ? <span>{text}</span> : <span className='vl-muted'>-</span>),
    },
    {
      title: translations[language]?.line,
      dataIndex: 'line',
      key: 'line',
      render: (text) => (text ? lineBadge(text) : <span className='vl-muted'>-</span>),
    },
    {
      title: translations[language]?.route,
      dataIndex: 'route_long_name',
      key: 'routeLongName',
      render: (text) => (text ? <span>{text}</span> : <span className='vl-muted'>-</span>),
    },
    ...(canManage
      ? [
          {
            title: translations[language]?.action,
            key: 'action',
            width: 100,
            render: (_, record) => (
              <div className='vl-actions'>
                <button
                  type='button'
                  className='vl-action-btn vl-action-delete'
                  onClick={() => setDetachTarget(record)}
                  title={translations[language]?.detachVehicle}
                >
                  <FaUnlink size={14} />
                </button>
                {!isVersion && (
                  <button
                    type='button'
                    className='vl-action-btn vl-action-edit'
                    onClick={() => openReassign(record)}
                    title={translations[language]?.changeLineTrip}
                  >
                    <FaExchangeAlt size={14} />
                  </button>
                )}
              </div>
            ),
          },
        ]
      : []),
  ]

  const vehicleDetailRow = (label, value) => (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 16,
        padding: '10px 0',
        borderBottom: '1px solid #f0f0f0',
      }}
    >
      <span style={{ color: '#888', fontSize: 13 }}>{label}</span>
      <span style={{ fontWeight: 600, textAlign: 'right' }}>{value ?? '-'}</span>
    </div>
  )

  return (
    <div className='main-container'>
      <div className='main-inner-container'>
        <div className='insider-container-buttons report-header-div'>
          <h1 className='main-heading'>{translations[language]?.gtfsDetails}</h1>
        </div>
        <div className='insider-container'>
          <div className='insider-container-buttons'>
            <div className='vehicle-buttons-div'>
              <button className='add-button' onClick={() => history.push(allPaths?.GTFS)}>
                <FaArrowLeft className='icons' />
                {translations[language]?.backToGtfs}
              </button>
              <button className='add-button' onClick={() => setVersionsModal(true)}>
                <FaHistory className='icons' />
                {translations[language]?.versions}
              </button>
              {!isVersion && canManage && (
                <button className='add-button' onClick={openCoordsModal}>
                  <FaMapMarkerAlt className='icons' />
                  {translations[language]?.stopCoordinates}
                </button>
              )}
              {isVersion && (
                <button
                  className='add-button'
                  onClick={() =>
                    history.push({
                      pathname: `${allPaths?.GTFS_DETAILS}/${encodeURIComponent(baseFeed)}`,
                      state: { feedName: location?.state?.feedName },
                    })
                  }
                >
                  {translations[language]?.currentVersion || 'Current version'}
                </button>
              )}
            </div>
          </div>
          <div className='table-container'>
            <div className='table-inner-container'>
              <div className='table-header margin-bottom-20' style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <h1 className='h1-theme-color vehicle-list-heading margin-bottom-0'>{location?.state?.feedName || feed.replace(/__v\d+$/, '')}</h1>
                <div className='gd-feed-chips'>
                  {feedOrgName && (
                    <span className='gl-chip' title={translations[language]?.organization}>
                      <strong>{feedOrgName}</strong>
                    </span>
                  )}
                  <span className='gl-chip'>
                    <strong>{lines?.length || 0}</strong> {translations[language]?.routes}
                  </span>
                  {!isVersion && (
                    <span className='gl-chip'>
                      <strong>{vehicles?.length || 0}</strong> {translations[language]?.attachedVehicles}
                    </span>
                  )}
                </div>
                {isVersion && (
                  <span
                    style={{
                      background: '#fff7e6',
                      border: '1px solid #ffd591',
                      color: '#ad6800',
                      borderRadius: 12,
                      padding: '2px 12px',
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    {translations[language]?.previousVersion || 'Previous version'} v{versionNo}
                  </span>
                )}
              </div>

              <Tabs activeKey={activeTab} onChange={setActiveTab}>
                <Tabs.TabPane tab={translations[language]?.routes} key='routes'>
                  <div className='gd-vehicles-toolbar'>
                    <div className='gd-vehicles-tools'>
                      <Input
                        allowClear
                        className='rh-filter-input gd-vehicle-search'
                        prefix={<FaSearch className='rh-search-icon' />}
                        placeholder={translations[language]?.searchRoute}
                        value={lineSearch}
                        onChange={(e) => { setLineSearch(e.target.value); setLinesPageNo(1) }}
                        aria-label={translations[language]?.searchRoute}
                      />
                      <span className='vl-total-chip'>
                        {translations[language]?.totalRecord}: <strong>{visibleLines.length}</strong>
                        {lineSearch.trim() && visibleLines.length !== lines.length ? ` / ${lines.length}` : ''}
                      </span>
                    </div>
                  </div>
                  {loading && lines.length === 0 ? (
                    <Skeleton active paragraph={{ rows: 6 }} />
                  ) : (
                    <Table
                      className='vl-table'
                      dataSource={visibleLines}
                      columns={columns}
                      rowKey='route_id'
                      pagination={{
                        pageSize: 10,
                        showSizeChanger: false,
                        current: linesPageNo,
                        onChange: setLinesPageNo,
                      }}
                      scroll={{ x: 'max-content' }}
                      loading={loading}
                    />
                  )}
                </Tabs.TabPane>
                <Tabs.TabPane tab={translations[language]?.shifts} key='shifts'>
                  <div className='gs-tab-head'>
                    <span className='vl-total-chip'>
                      {translations[language]?.totalRecord}: <strong>{allShifts?.length || 0}</strong>
                    </span>
                    {!allShiftsLoading && allShifts.some((sh) => sh?.live) && (
                      <span className='dash-status-pill dash-status-ok'>
                        <span className='dash-status-dot' />
                        {allShifts.filter((sh) => sh?.live).length} {translations[language]?.liveNow}
                      </span>
                    )}
                  </div>
                  {allShiftsLoading ? (
                    <div style={{ clear: 'both', paddingTop: 8 }}>
                      {[0, 1, 2].map((i) => (
                        <div
                          key={i}
                          style={{
                            border: '1px solid #f0f0f0',
                            borderLeft: '4px solid #f0f0f0',
                            borderRadius: 6,
                            padding: '10px 16px',
                            marginBottom: 14,
                          }}
                        >
                          <Skeleton active title paragraph={{ rows: 3 }} />
                        </div>
                      ))}
                    </div>
                  ) : allShifts.length === 0 ? (
                    <div style={{ color: '#888', clear: 'both', paddingTop: 8 }}>
                      {translations[language]?.noShifts}
                    </div>
                  ) : (
                    <div style={{ clear: 'both' }}>
                      {renderShiftCards(
                        allShifts.slice(
                          (Math.min(shiftsPage, Math.max(1, Math.ceil(allShifts.length / SHIFTS_PAGE_SIZE))) - 1) * SHIFTS_PAGE_SIZE,
                          Math.min(shiftsPage, Math.max(1, Math.ceil(allShifts.length / SHIFTS_PAGE_SIZE))) * SHIFTS_PAGE_SIZE
                        ),
                        true
                      )}
                      {allShifts.length > SHIFTS_PAGE_SIZE && (
                        <div style={{ display: 'flex', justifyContent: 'center', padding: '8px 0 16px' }}>
                          <Pagination
                            current={Math.min(shiftsPage, Math.max(1, Math.ceil(allShifts.length / SHIFTS_PAGE_SIZE)))}
                            pageSize={SHIFTS_PAGE_SIZE}
                            total={allShifts.length}
                            showSizeChanger={false}
                            onChange={setShiftsPage}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </Tabs.TabPane>
                {!isVersion && (
                <Tabs.TabPane tab={translations[language]?.attachedVehicles} key='vehicles'>
                  <div className='gd-vehicles-toolbar'>
                    {canManage && (
                      <button className='add-button' onClick={openAttachModal}>
                        <FaLink className='icons' />
                        {translations[language]?.attachVehicle}
                      </button>
                    )}
                    {canManage && !isVersion && vehicles.length > 0 && (
                      <button className='add-button' onClick={openDetectModal}>
                        <FaRoute className='icons' />
                        {translations[language]?.detectLines}
                      </button>
                    )}
                    <div className='gd-vehicles-tools'>
                      <Input
                        allowClear
                        className='rh-filter-input gd-vehicle-search'
                        prefix={<FaSearch className='rh-search-icon' />}
                        placeholder={translations[language]?.searchVehicle}
                        value={vehicleSearch}
                        onChange={(e) => { setVehicleSearch(e.target.value); setVehiclesPageNo(1) }}
                        aria-label={translations[language]?.searchVehicle}
                      />
                      <span className='vl-total-chip'>
                        {translations[language]?.totalRecord}: <strong>{visibleVehicles.length}</strong>
                        {vehicleSearch.trim() && visibleVehicles.length !== vehicles.length ? ` / ${vehicles.length}` : ''}
                      </span>
                    </div>
                  </div>
                  {vehiclesLoading && vehicles.length === 0 ? (
                    <Skeleton active paragraph={{ rows: 6 }} />
                  ) : (
                    <Table
                      className='vl-table'
                      dataSource={visibleVehicles}
                      columns={vehicleColumns}
                      rowKey='id'
                      pagination={{
                        pageSize: 10,
                        showSizeChanger: false,
                        current: vehiclesPageNo,
                        onChange: setVehiclesPageNo,
                      }}
                      scroll={{ x: 'max-content' }}
                      loading={vehiclesLoading}
                    />
                  )}
                </Tabs.TabPane>
                )}
              </Tabs>
            </div>
          </div>
        </div>
      </div>

      <Drawer
        placement='right'
        width={500}
        visible={!!selectedLine}
        onClose={closeDrawer}
        className='gl-drawer'
        title={
          selectedLine ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              {lineBadge(selectedLine?.line)}
              <span>{selectedLine?.route_long_name || ''}</span>
            </div>
          ) : null
        }
      >
        {detailLoading ? (
          <Skeleton active paragraph={{ rows: 6 }} />
        ) : (
          <>
            {/* Line at a glance: the figures the routes table already holds. */}
            <div className='gl-summary'>
              <span className='gl-chip'>
                <strong>{(lineDetail?.directions || []).length}</strong> {translations[language]?.direction}
              </span>
              {selectedLine?.trips !== undefined && (
                <span className='gl-chip'>
                  <strong>{selectedLine?.trips ?? 0}</strong> {translations[language]?.trips}
                </span>
              )}
              {selectedLine?.stops_served !== undefined && (
                <span className='gl-chip'>
                  <strong>{selectedLine?.stops_served ?? 0}</strong> {translations[language]?.stops}
                </span>
              )}
              {selectedLine?.first_departure && (
                <span className='gl-chip'>
                  {translations[language]?.firstDeparture} <strong>{formatGtfsTime(selectedLine.first_departure)}</strong>
                </span>
              )}
              {selectedLine?.last_arrival && (
                <span className='gl-chip'>
                  {translations[language]?.lastArrival} <strong>{formatGtfsTime(selectedLine.last_arrival)}</strong>
                </span>
              )}
            </div>

            <div className='gl-view-switch'>
              <button
                type='button'
                className={`gl-view-btn${lineView === 'stops' ? ' active' : ''}`}
                onClick={() => setLineView('stops')}
              >
                <FaListUl /> {translations[language]?.stopsView}
              </button>
              <button
                type='button'
                className={`gl-view-btn${lineView === 'map' ? ' active' : ''}`}
                onClick={() => setLineView('map')}
              >
                <FaMapMarkedAlt /> {translations[language]?.mapView}
              </button>
            </div>

            {lineView === 'map' ? renderLineMap() : (lineDetail?.directions || []).map((direction) => {
              const stops = direction?.stops || []
              return (
                <div key={direction?.direction_id} className='gl-direction'>
                  <div className='gl-direction-head'>
                    <div className='gl-direction-title'>
                      <span className='gl-direction-arrow'>→</span>
                      <span>{direction?.headsign || `${translations[language]?.direction} ${direction?.direction_id}`}</span>
                    </div>
                    <div className='gl-direction-meta'>
                      <span className='gl-chip gl-chip-sm'>
                        <strong>{stops.length}</strong> {translations[language]?.stops}
                      </span>
                      {direction?.duration_minutes !== null && direction?.duration_minutes !== undefined && (
                        <span className='gl-chip gl-chip-sm'>
                          <strong>{direction.duration_minutes}</strong> min
                        </span>
                      )}
                    </div>
                  </div>
                  <div className='gl-timeline'>
                    {stops.map((stop, i) => {
                      const isEnd = i === 0 || i === stops.length - 1
                      return (
                        <div
                          key={`${direction?.direction_id}-${stop?.stop_sequence}`}
                          className={`gl-stop${isEnd ? ' gl-stop-end' : ''}`}
                        >
                          <span className='gl-stop-time'>{formatGtfsTime(stop?.departure_time || stop?.arrival_time)}</span>
                          <span className='gl-stop-rail'>
                            <span className='gl-stop-dot' />
                            {i < stops.length - 1 && <span className='gl-stop-line' />}
                          </span>
                          <span className='gl-stop-body'>
                            <span className='gl-stop-name'>{stop?.stop_name || stop?.gtfs_stop_id}</span>
                            <span className='gl-stop-seq'>#{stop?.stop_sequence}</span>
                          </span>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )
            })}

            <div className='gl-shifts'>
              <div className='gl-direction-head'>
                <div className='gl-direction-title'>{translations[language]?.shifts}</div>
                {!shiftsLoading && (
                  <span className='gl-chip gl-chip-sm'><strong>{lineShifts.length}</strong></span>
                )}
              </div>
              {shiftsLoading ? (
                <Skeleton active title paragraph={{ rows: 3 }} />
              ) : lineShifts.length === 0 ? (
                <div className='gl-empty'>{translations[language]?.noShifts}</div>
              ) : (
                renderShiftCards(lineShifts, false)
              )}
            </div>
          </>
        )}
      </Drawer>

      <Drawer
        placement='right'
        width={480}
        visible={!!journeyDrawer}
        onClose={() => {
          setJourneyDrawer(null)
          setExpandedTripId(null)
        }}
        className='gl-drawer'
        title={
          journeyDrawer ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
              {journeyDrawer?.shift?.line || selectedLine?.line
                ? lineBadge(journeyDrawer?.shift?.line || selectedLine?.line)
                : null}
              <span className='text-ellipsis'>{journeyDrawer?.journey?.headsign || journeyDrawer?.journey?.trip_id}</span>
            </div>
          ) : null
        }
      >
        {journeyDrawer && (() => {
          const journey = journeyDrawer.journey
          const shift = journeyDrawer.shift
          const stops = tripStops[journey?.trip_id] || []
          const loading = tripStopsLoading === journey?.trip_id
          return (
            <>
              <div className='gl-summary'>
                <span className='gl-chip'>
                  <strong>{journey?.starts || '-'}</strong> - <strong>{journey?.ends || '-'}</strong>
                </span>
                <span className='gl-chip'>
                  {shift?.block_id
                    ? <>{translations[language]?.shift} <strong>{shift.block_id}</strong></>
                    : translations[language]?.noShiftAssigned}
                </span>
                {!loading && (
                  <span className='gl-chip'>
                    <strong>{stops.length}</strong> {translations[language]?.stops}
                  </span>
                )}
                {journey?.live && (
                  <span className='dash-status-pill dash-status-ok'>
                    <span className='dash-status-dot' />
                    {translations[language]?.liveNow}
                    {journey?.vehicle_name ? ` · ${journey.vehicle_name}` : ''}
                  </span>
                )}
              </div>
              <div className='gl-direction'>
                <div className='gl-direction-head'>
                  <div className='gl-direction-title'>
                    <span className='gl-direction-arrow'>→</span>
                    <span>{journey?.headsign || journey?.trip_id}</span>
                  </div>
                  <span className='gl-chip gl-chip-sm'>#{journey?.trip_id}</span>
                </div>
                {loading ? (
                  <Skeleton active paragraph={{ rows: 6 }} />
                ) : (
                  <div className='gl-timeline'>
                    {stops.map((stop, i) => {
                      const isEnd = i === 0 || i === stops.length - 1
                      return (
                        <div key={stop?.stop_sequence} className={`gl-stop${isEnd ? ' gl-stop-end' : ''}`}>
                          <span className='gl-stop-time'>{formatGtfsTime(stop?.departure_time || stop?.arrival_time)}</span>
                          <span className='gl-stop-rail'>
                            <span className='gl-stop-dot' />
                            {i < stops.length - 1 && <span className='gl-stop-line' />}
                          </span>
                          <span className='gl-stop-body'>
                            <span className='gl-stop-name'>{stop?.stop_name || stop?.gtfs_stop_id}</span>
                            <span className='gl-stop-seq'>#{stop?.stop_sequence}</span>
                          </span>
                        </div>
                      )
                    })}
                    {stops.length === 0 && (
                      <div className='gl-empty'>{translations[language]?.noRouteData || '-'}</div>
                    )}
                  </div>
                )}
              </div>
            </>
          )
        })()}
      </Drawer>

      <Drawer
        placement='right'
        width={520}
        visible={!!selectedVehicle}
        onClose={() => setSelectedVehicle(null)}
        title={translations[language]?.vehicleDetails}
      >
        {selectedVehicle && (
          <div>
            <div style={{ marginBottom: 16 }}>
              {selectedVehicle?.line ? lineBadge(selectedVehicle.line) : null}
            </div>
            {vehicleDetailRow(
              translations[language]?.vehicleName,
              selectedVehicle?.vehicle_name || selectedVehicle?.license_plate
            )}
            {vehicleDetailRow(translations[language]?.licensePlate, selectedVehicle?.license_plate)}
            {vehicleDetailRow(
              translations[language]?.registeringNumber,
              selectedVehicle?.registering_number
            )}
            {vehicleDetailRow(translations[language]?.line, selectedVehicle?.line)}
            {selectedVehicle?.line &&
              vehicleDetailRow(
                translations[language]?.lineStatus,
                <span
                  style={{
                    color: selectedVehicle?.gtfs_route_confirmed ? '#52c41a' : '#fa8c16',
                    fontWeight: 700,
                  }}
                >
                  {selectedVehicle?.gtfs_route_confirmed
                    ? translations[language]?.confirmed
                    : translations[language]?.unconfirmed}
                </span>
              )}
            {vehicleDetailRow(translations[language]?.route, selectedVehicle?.route_long_name)}
            {vehicleDetailRow(translations[language]?.trip, selectedVehicle?.gtfs_trip_id)}
            {selectedVehicle?.gtfs_manual_until && new Date(selectedVehicle.gtfs_manual_until) > new Date()
              ? vehicleDetailRow(
                  translations[language]?.manualAssignment,
                  `${translations[language]?.lockedUntil} ${moment(selectedVehicle.gtfs_manual_until).format('DD/MM/YYYY HH:mm')}`
                )
              : null}
            {canManage && !isVersion && (
              <div className='gd-reassign-actions'>
                <button type='button' className='gd-reassign-btn' onClick={() => openReassign(selectedVehicle)}>
                  <FaExchangeAlt /> {translations[language]?.changeLineTrip}
                </button>
                <button type='button' className='gd-reassign-btn gd-reassign-btn-danger' onClick={() => setDetachTarget(selectedVehicle)}>
                  <FaUnlink /> {translations[language]?.detachVehicle}
                </button>
              </div>
            )}
            {canManage && (
              <div className='gd-history'>
                <div className='gd-history-title'>{translations[language]?.assignmentHistory}</div>
                {assignmentHistory.length ? (
                  assignmentHistory.map((h) => (
                    <div className='gd-history-row' key={h.id}>
                      <span className='gd-history-when'>{moment(h.created_at).format('DD/MM HH:mm')}</span>
                      <span className='gd-history-change'>
                        {h.from_line || '-'}{h.from_trip_id ? ` / ${h.from_trip_id}` : ''}
                        {' → '}
                        <strong>{h.to_line || h.to_route_id}</strong>{h.to_trip_id ? ` / ${h.to_trip_id}` : ''}
                        {h.forced ? ` (${translations[language]?.forced})` : ''}
                      </span>
                      <span className='gd-history-who'>{h.changed_by_name || h.changed_by_email || ''}</span>
                    </div>
                  ))
                ) : (
                  <div className='vl-muted'>{translations[language]?.noAssignmentHistory}</div>
                )}
              </div>
            )}
            {vehicleDetailRow(
              translations[language]?.lastSeen,
              selectedVehicle?.last_seen_at && moment(selectedVehicle.last_seen_at).isValid()
                ? moment(selectedVehicle.last_seen_at).format('DD/MM/YYYY HH:mm:ss')
                : null
            )}

            <div className='device-drawer-map-section' style={{ marginTop: 16 }}>
              {hasVehicleLocation || vehicleLineDirections.length > 0 ? (
                <div className='device-drawer-map'>
                  <VehicleMap
                    deviceInfo={vehicleDeviceInfo}
                    vehicleDetails={{
                      id: selectedVehicle?.id,
                      logo: vehicleDeviceInfo?.logoUrl || null,
                      vehicle_name:
                        selectedVehicle?.vehicle_name ||
                        vehicleDeviceInfo?.vehicleName ||
                        selectedVehicle?.license_plate ||
                        '-',
                      vehicle_model: vehicleDeviceInfo?.vehicleModel || '-',
                      registering_number: selectedVehicle?.registering_number || null,
                    }}
                    language={language}
                    selectedDate={vehicleMapDate}
                    isLoading={vehicleDeviceLoading}
                    lineDirections={vehicleLineDirections}
                  />
                  {!hasVehicleLocation && !vehicleDeviceLoading && (
                    <div className='device-drawer-map-note'>
                      {translations[language]?.noVehicleLocation}
                    </div>
                  )}
                </div>
              ) : vehicleMapLoading ? (
                <div className='device-drawer-map-placeholder'>
                  <Spin />
                  <span>{translations[language]?.loadingDeviceLocation}</span>
                </div>
              ) : (
                <div className='device-drawer-map-placeholder device-drawer-map-empty'>
                  {translations[language]?.noVehicleMapData}
                </div>
              )}
            </div>
          </div>
        )}
      </Drawer>

      <CustomModal
        isVisible={attachModal}
        onClose={() => setAttachModal(false)}
        title={translations[language]?.attachVehicle}
        actionText={translations[language]?.attachVehicle}
        onAction={handleAttach}
        loading={attachSaving}
        language={language}
      >
        <div style={{ padding: '8px 0' }}>
          {isSuperAdmin && (
            // Read-only: a feed belongs to one organization, so only that
            // organization's vehicles can be attached to it.
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>
                {translations[language]?.organization}
              </label>
              <Select
                style={{ width: '100%' }}
                disabled
                value={feedOrgId}
                options={(allOrganizations || []).map((org) => ({
                  value: org?.id,
                  label: org?.organization_name,
                }))}
              />
              <div className='rp-empty-hint' style={{ marginTop: 6 }}>
                {translations[language]?.attachOrgHint}
              </div>
            </div>
          )}
          <label style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>
            {translations[language]?.selectVehicle}
          </label>
          <Select
            style={{ width: '100%' }}
            mode='multiple'
            showSearch
            allowClear
            maxTagCount='responsive'
            optionFilterProp='label'
            loading={attachOptionsLoading}
            placeholder={translations[language]?.selectVehiclePlaceholder}
            value={attachVehicleIds}
            onChange={setAttachVehicleIds}
            notFoundContent={attachOptionsLoading ? null : translations[language]?.noVehiclesToAttach}
            options={attachOptions.map((v) => ({
              value: v?.id,
              disabled: !!v?.gtfs_feed_source,
              label: `${vehicleLabel(v) || v?.id}${
                v?.gtfs_feed_source
                  ? ` — ${translations[language]?.attachedToOtherGtfs} ${feedNameOf(v.gtfs_feed_source)}`
                  : ''
              }`,
            }))}
            dropdownRender={(menu) => (
              <>
                {attachableOptions.length > 0 && (
                  <SelectAllRow
                    label={translations[language]?.selectAll}
                    total={attachableOptions.length}
                    selected={attachVehicleIds.length}
                    onToggle={(checked) =>
                      setAttachVehicleIds(checked ? attachableOptions.map((v) => v?.id) : [])
                    }
                  />
                )}
                {menu}
              </>
            )}
          />
          {attachableOptions.length > 0 && (
            <div className='rp-empty-hint' style={{ marginTop: 6 }}>
              {attachVehicleIds.length} / {attachableOptions.length}
            </div>
          )}
          {attachOptions.length > attachableOptions.length && (
            <div className='rp-empty-hint' style={{ marginTop: 6 }}>
              {translations[language]?.attachLockedHint}
            </div>
          )}
        </div>
      </CustomModal>

      <CustomModal
        isVisible={detectModal}
        onClose={() => setDetectModal(false)}
        title={translations[language]?.detectLines}
        language={language}
        actionText={detectResult && !detectLoading ? translations[language]?.assignSelected : null}
        onAction={handleAssignLines}
        loading={assignSaving}
      >
        <div className='gl-coords'>
          <p className='gl-coords-intro'>{translations[language]?.detectLinesIntro}</p>
          {detectLoading && (
            <div className='gd-detect-running'>
              <Spin size='small' />
              <span>{translations[language]?.detectionRunning}</span>
            </div>
          )}
          {detectResult && !detectLoading && (() => {
            const routeOptions = detectResult.routes || []
            const pct = (c) => `${Math.round((c || 0) * 100)}%`
            const minCov = detectResult.min_coverage || 0.25
            const lineOption = (r) => (
              <span className='gd-detect-opt'>
                {lineBadge(r.line)}
                {r.route_long_name && <span className='gd-detect-opt-name'>{r.route_long_name}</span>}
              </span>
            )
            const columns = [
              {
                title: translations[language]?.vehicleName,
                key: 'vehicle',
                render: (_, v) => (
                  <div>
                    <div className='gd-detect-vehicle'>{v.vehicle_name || v.license_plate || '-'}</div>
                    <div className='gd-detect-sub'>
                      {[v.license_plate, v.registering_number].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                ),
              },
              {
                title: translations[language]?.keepCurrent,
                key: 'current',
                render: (_, v) =>
                  v.current_line ? (
                    <span className='gd-detect-opt'>
                      {lineBadge(v.current_line)}
                      <span className='gd-detect-sub'>
                        {v.current_confirmed ? translations[language]?.confirmed : translations[language]?.unconfirmed}
                      </span>
                    </span>
                  ) : (
                    <span className='vl-muted'>-</span>
                  ),
              },
              {
                title: translations[language]?.detectedLine,
                key: 'detected',
                render: (_, v) => {
                  if (!v.detected) {
                    const top = v.candidates?.[0]
                    return (
                      <div>
                        <div className='gd-detect-reason'>
                          {translations[language]?.detectReason?.[v.reason] || v.reason || '-'}
                        </div>
                        {top && (
                          <div className='gd-detect-sub'>
                            {translations[language]?.runnerUp}: {top.line} {pct(top.coverage)}
                          </div>
                        )}
                      </div>
                    )
                  }
                  const second = v.candidates?.[1]
                  return (
                    <div>
                      <span className='gd-detect-opt'>
                        {lineBadge(v.detected.line)}
                        {v.detected.route_long_name && (
                          <span className='gd-detect-opt-name'>{v.detected.route_long_name}</span>
                        )}
                      </span>
                      <div className='gd-detect-cov' style={{ marginTop: 6 }}>
                        <span className={`gd-detect-bar${v.detected.coverage < minCov * 2 ? ' low' : ''}`}>
                          <span style={{ width: pct(v.detected.coverage) }} />
                        </span>
                        <span className='gd-detect-pct'>{pct(v.detected.coverage)}</span>
                      </div>
                      <div className='gd-detect-sub'>
                        {v.detected.covered_stops}/{v.detected.total_stops} {translations[language]?.stopsCovered}
                        {second ? ` · ${translations[language]?.runnerUp}: ${second.line} ${pct(second.coverage)}` : ''}
                      </div>
                    </div>
                  )
                },
              },
              {
                title: translations[language]?.assignLine,
                key: 'assign',
                width: 240,
                render: (_, v) => (
                  <Select
                    className='gd-detect-select'
                    allowClear
                    showSearch
                    optionFilterProp='label'
                    placeholder={translations[language]?.clearLine}
                    value={detectChoice[v.vehicle_id] || undefined}
                    onChange={(value) => {
                      setDetectChoice((prev) => ({ ...prev, [v.vehicle_id]: value || null }))
                      setDetectSelected((prev) => (prev.includes(v.vehicle_id) ? prev : [...prev, v.vehicle_id]))
                    }}
                    options={routeOptions.map((r) => ({
                      value: r.route_id,
                      label: `${r.line}${r.route_long_name ? ` ${r.route_long_name}` : ''}`,
                    }))}
                  />
                ),
              },
            ]
            return (
              <>
                <div className='gd-detect-toolbar'>
                  <div className='gl-summary' style={{ marginBottom: 0 }}>
                    <span className='gl-chip'>
                      <strong>{detectResult.history_days}</strong> {translations[language]?.historyDays}
                    </span>
                    <span className='gl-chip'>
                      <strong>{(detectResult.vehicles || []).filter((v) => v.detected).length}</strong> /{' '}
                      {(detectResult.vehicles || []).length} {translations[language]?.detectedLine}
                    </span>
                    <span className='gl-chip'>
                      <strong>{detectSelected.length}</strong> {translations[language]?.assignSelected}
                    </span>
                  </div>
                  <button type='button' className='add-button' onClick={runDetection}>
                    <FaRedo className='icons' />
                    {translations[language]?.runDetection}
                  </button>
                </div>
                <Table
                  className='gd-detect-table'
                  size='small'
                  dataSource={detectResult.vehicles || []}
                  columns={columns}
                  rowKey='vehicle_id'
                  pagination={false}
                  scroll={{ x: 'max-content' }}
                  rowSelection={{
                    selectedRowKeys: detectSelected,
                    onChange: (keys) => setDetectSelected(keys),
                  }}
                />
              </>
            )
          })()}
        </div>
      </CustomModal>

      <CustomModal
        isVisible={coordsModal}
        onClose={closeCoordsModal}
        title={translations[language]?.stopCoordinates}
        language={language}
        actionText={coordsResult ? null : translations[language]?.resolveCoordinates}
        onAction={handleResolveCoords}
        loading={coordsSaving}
      >
        <div className='gl-coords'>
          <p className='gl-coords-intro'>{translations[language]?.stopCoordinatesIntro}</p>
          {coordsProgress && (
            <div className='gl-coords-progress'>
              <Spin size='small' />
              <span>
                {translations[language]?.geocodeProgress} <strong>{coordsProgress.remaining}</strong>
              </span>
            </div>
          )}
          {!coordsResult && (
            <div className='gl-coords-form'>
              {coordsRetry && (
                <div className='gl-coords-retry'>
                  <FaRedo className='gl-coords-retry-icon' />
                  <span>
                    <strong>{coordsRetry.unresolved}</strong> {translations[language]?.coordsRetryNote}
                  </span>
                </div>
              )}

              <div className='gl-coords-card'>
                <div className='gl-coords-card-title'>{translations[language]?.stopCoordinatesFiles}</div>
                <div className='gl-coords-card-hint'>{translations[language]?.stopsFileHint}</div>
                <Upload.Dragger
                  accept='.csv,.txt,.zip'
                  multiple
                  fileList={coordsFiles}
                  beforeUpload={(file) => {
                    setCoordsFiles((prev) => [...prev, file])
                    return false
                  }}
                  onRemove={(file) => setCoordsFiles((prev) => prev.filter((f) => f.uid !== file.uid))}
                  className='gl-coords-dragger'
                >
                  <p className='ant-upload-drag-icon'>
                    <UploadOutlined />
                  </p>
                  <p className='ant-upload-text'>{translations[language]?.selectStopsFile}</p>
                  <p className='ant-upload-hint'>.csv · .txt · .zip</p>
                </Upload.Dragger>
              </div>

              <div className='gl-coords-card gl-coords-geocode'>
                <Checkbox checked={coordsGeocode} onChange={(e) => setCoordsGeocode(e.target.checked)}>
                  {translations[language]?.geocodeRest}
                </Checkbox>
                <div className='gl-coords-hint'>{translations[language]?.geocodeRestHint}</div>
              </div>
            </div>
          )}
          {coordsResult && (() => {
            const referenceCount =
              (coordsResult.reference?.platform || 0) + (coordsResult.reference?.files || 0)
            return (
              <div className='gl-coords-result'>
                <div className='gl-summary'>
                  <span className='gl-chip'>
                    <strong>{coordsResult.resolved}</strong> {translations[language]?.coordsResolvedNow}
                  </span>
                  {coordsResult.geocoded > 0 && (
                    <span className='gl-chip'>
                      <strong>{coordsResult.geocoded}</strong> {translations[language]?.coordsGeocoded}
                    </span>
                  )}
                  {coordsResult.geocode_cached > 0 && (
                    <span className='gl-chip'>
                      <strong>{coordsResult.geocode_cached}</strong> {translations[language]?.coordsGeocodeCached}
                    </span>
                  )}
                  <span className='gl-chip'>
                    <strong>{coordsResult.unresolved}</strong> {translations[language]?.coordsStillMissing}
                  </span>
                  <span className='gl-chip'>
                    <strong>{coordsResult.placeholder_stops}</strong> {translations[language]?.coordsPlaceholders}
                  </span>
                  <span className='gl-chip'>
                    <strong>{coordsResult.reference?.platform ?? 0}</strong> {translations[language]?.coordsReferencePlatform}
                  </span>
                  {coordsResult.reference?.files > 0 && (
                    <span className='gl-chip'>
                      <strong>{coordsResult.reference.files}</strong> {translations[language]?.coordsReferenceFiles}
                    </span>
                  )}
                </div>
                {coordsResult.targeted === 0 && (
                  <div className='gl-empty'>{translations[language]?.coordsNothingToDo}</div>
                )}
                {/* Only once the run is over: while names are still being
                    looked up the counts are moving. */}
                {!coordsProgress && !coordsSaving && coordsResult.unresolved > 0 && (
                  <div className='device-drawer-map-placeholder device-drawer-map-empty gl-map-empty'>
                    {coordsGeocode || coordsResult.geocoded > 0
                      ? translations[language]?.coordsGeocodeMissing
                      : referenceCount === 0
                        ? translations[language]?.coordsNoReference
                        : translations[language]?.coordsGeocodeMissing}
                  </div>
                )}
                {!coordsProgress && !coordsSaving && coordsResult.unresolved_stops?.length > 0 && (
                  <details className='gl-coords-unresolved'>
                    <summary>
                      {translations[language]?.coordsStillMissing} ({coordsResult.unresolved_stops.length})
                    </summary>
                    <ul>
                      {coordsResult.unresolved_stops.map((s) => (
                        <li key={s.gtfs_stop_id}>
                          {s.stop_name}
                          {s.stop_code ? ` (${s.stop_code})` : ''}
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
                {!coordsProgress && !coordsSaving && coordsResult.targeted > 0 && coordsResult.unresolved > 0 && (
                  <button
                    type='button'
                    className='add-button'
                    onClick={() => {
                      setCoordsRetry({ unresolved: coordsResult.unresolved })
                      setCoordsResult(null)
                      setCoordsFiles([])
                    }}
                  >
                    <UploadOutlined className='icons' />
                    {translations[language]?.tryAgain}
                  </button>
                )}
              </div>
            )
          })()}
        </div>
      </CustomModal>

      <DeleteModal
        title={translations[language]?.detachVehicle}
        bodyText={
          <>
            {translations[language]?.areYouSureDetachVehicle}
            {detachTarget?.vehicle_name || detachTarget?.license_plate
              ? ` (${detachTarget?.vehicle_name || detachTarget?.license_plate})`
              : ''}
          </>
        }
        isOpen={!!detachTarget}
        onCancel={() => setDetachTarget(null)}
        onDelete={handleDetach}
        language={language}
      />

      <CustomModal
        isVisible={!!reassignTarget}
        onClose={closeReassign}
        title={translations[language]?.changeLineTrip}
        actionText={translations[language]?.applyChange}
        onAction={confirmReassign}
        loading={reassignSaving}
        language={language}
      >
        {reassignTarget && (
          <div className='gd-reassign'>
            <div className='gd-reassign-current'>
              <div className='gd-reassign-vehicle'>
                {reassignTarget?.vehicle_name || reassignTarget?.license_plate || '-'}
                {reassignTarget?.registering_number ? (
                  <span className='gd-reassign-sub'>{reassignTarget.registering_number}</span>
                ) : null}
              </div>
              <div className='gd-reassign-now'>
                <span className='gd-reassign-label'>{translations[language]?.currentLine}:</span>
                {reassignTarget?.line ? lineBadge(reassignTarget.line) : <span className='vl-muted'>-</span>}
                {reassignTarget?.gtfs_trip_id ? (
                  <span className='gd-reassign-trip'>
                    {translations[language]?.trip}: {reassignTarget.gtfs_trip_id}
                  </span>
                ) : null}
              </div>
            </div>

            <label className='gd-reassign-field'>{translations[language]?.newLine}</label>
            <Select
              showSearch
              optionFilterProp='label'
              style={{ width: '100%' }}
              placeholder={translations[language]?.selectLine}
              value={reassignRoute || undefined}
              onChange={(value) => {
                setReassignRoute(value || null)
                setReassignTrip(null)
                fetchReassignShifts(value || null)
              }}
              options={(lines || []).map((l) => ({
                value: l?.route_id,
                label: `${l?.line || l?.route_id}${l?.route_long_name ? ` ${l.route_long_name}` : ''}`,
              }))}
            />

            <label className='gd-reassign-field'>
              {translations[language]?.newTrip}
              <span className='gd-reassign-opt'> ({translations[language]?.optionalWholeLine})</span>
            </label>
            <Select
              showSearch
              allowClear
              optionFilterProp='label'
              style={{ width: '100%' }}
              loading={reassignShiftsLoading}
              disabled={!reassignRoute}
              placeholder={translations[language]?.selectTrip}
              notFoundContent={reassignShiftsLoading ? <Spin size='small' /> : translations[language]?.noActiveTrips}
              value={reassignTrip || undefined}
              onChange={(value) => setReassignTrip(value || null)}
              options={reassignTripOptions}
            />

            <Checkbox
              className='gd-reassign-force'
              checked={reassignForce}
              onChange={(e) => setReassignForce(e.target.checked)}
            >
              {translations[language]?.forceReassign}
            </Checkbox>

            <Input
              className='gd-reassign-reason'
              placeholder={translations[language]?.reasonOptional}
              value={reassignReason}
              maxLength={200}
              onChange={(e) => setReassignReason(e.target.value)}
            />

            <div className='gd-reassign-note'>ⓘ {translations[language]?.reassignNote}</div>
          </div>
        )}
      </CustomModal>

      <GtfsVersionsModal
        isVisible={versionsModal}
        onClose={() => setVersionsModal(false)}
        feed={{ feed_source: baseFeed, feed_name: location?.state?.feedName }}
        language={language}
        onView={(version) => {
          setVersionsModal(false)
          history.push({
            pathname: `${allPaths?.GTFS_DETAILS}/${encodeURIComponent(version?.feed_source)}`,
            state: { feedName: location?.state?.feedName || version?.feed_name },
          })
        }}
      />
    </div>
  )
}

export default GtfsDetails
